import React from 'react';
import {
	Box,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Text,
	Flex,
	Icon,
	Badge,
	HStack,
	VStack,
} from '@chakra-ui/react';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const FeatureValue = ({ value, compact = false }) => {
	const iconSize = compact ? 5.5 : 7;
	const checkSize = compact ? 2.5 : 4;
	const crossSize = compact ? 2.5 : 3.5;

	if (!value) {
		return (
			<Flex justify='center'>
				<Flex
					align='center'
					justify='center'
					w={iconSize}
					h={iconSize}
					borderRadius='full'
					bg='red.50'>
					<Icon as={FaTimesCircle} color='red.400' boxSize={crossSize} />
				</Flex>
			</Flex>
		);
	}

	if (value === 'included') {
		return (
			<Flex justify='center'>
				<Flex
					align='center'
					justify='center'
					w={iconSize}
					h={iconSize}
					borderRadius='full'
					bg='green.50'>
					<Icon as={FaCheckCircle} color='green.500' boxSize={checkSize} />
				</Flex>
			</Flex>
		);
	}

	return (
		<Text
			fontWeight='600'
			fontSize={compact ? 'xs' : 'sm'}
			color='gray.700'
			m={0}
			lineHeight='short'>
			{value}
		</Text>
	);
};

const PlanColumnHeader = ({ plan, isYearly }) => {
	const monthly = plan.pricing?.monthly;
	const yearly = plan.pricing?.yearly;
	const savings = plan.pricing?.annualSavings;
	const lifetime = plan.pricing?.lifetime;
	const isLifetime = plan.isLifetimeAccessPlan || Boolean(lifetime);

	if (isLifetime) {
		return (
			<VStack spacing={1} py={1}>
				{plan.isPopular && (
					<Badge colorScheme='purple' fontSize='10px' borderRadius='full' px={2}>
						Most popular
					</Badge>
				)}
				<Text fontWeight='800' fontSize='sm' color='gray.800' m={0} lineHeight='short'>
					{plan.name}
				</Text>
				{lifetime?.formatted ? (
					<HStack spacing={0} align='baseline' justify='center'>
						<Text fontWeight='800' fontSize='lg' color='orange.500' m={0} lineHeight='1'>
							{lifetime.formatted}
						</Text>
						<Text fontSize='10px' color='gray.500' m={0}>
							once
						</Text>
					</HStack>
				) : (
					<Text fontSize='xs' color='gray.500' m={0}>
						Lifetime access
					</Text>
				)}
			</VStack>
		);
	}

	if (plan.isCustom) {
		return (
			<VStack spacing={1} py={1}>
				{plan.isPopular && (
					<Badge colorScheme='purple' fontSize='10px' borderRadius='full' px={2}>
						Most popular
					</Badge>
				)}
				<Text fontWeight='800' fontSize='sm' color='gray.800' m={0} lineHeight='short'>
					{plan.name}
				</Text>
				<Text fontSize='xs' color='gray.500' m={0}>
					Custom pricing
				</Text>
			</VStack>
		);
	}

	const displayPrice = isYearly
		? yearly?.formattedAnnual || yearly?.formatted
		: monthly?.formatted;

	return (
		<VStack spacing={1} py={1}>
			{plan.isPopular && (
				<Badge colorScheme='purple' fontSize='10px' borderRadius='full' px={2}>
					Most popular
				</Badge>
			)}
			<Text fontWeight='800' fontSize='sm' color='gray.800' m={0} lineHeight='short'>
				{plan.name}
			</Text>
			{displayPrice ? (
				<HStack spacing={0} align='baseline' justify='center'>
					<Text fontWeight='800' fontSize='lg' color='blue.600' m={0} lineHeight='1'>
						{displayPrice}
					</Text>
					<Text fontSize='10px' color='gray.500' m={0}>
						{isYearly ? '/yr' : '/mo'}
					</Text>
				</HStack>
			) : (
				<Text fontSize='xs' color='gray.500' m={0}>
					Contact sales
				</Text>
			)}
			{isYearly && savings?.formatted && (
				<Text fontSize='10px' color='green.600' fontWeight='600' m={0}>
					Save {savings.formatted}/yr
				</Text>
			)}
		</VStack>
	);
};

const PublicPricingMatrix = ({
	plans = [],
	featureRows = [],
	inModal = false,
	isYearly = false,
}) => {
	if (!plans.length || !featureRows.length) return null;

	const compact = inModal;

	const stickyHeaderSx = inModal
		? {
				position: 'sticky',
				top: 0,
				zIndex: 2,
				bg: 'gray.50',
				boxShadow: '0 1px 0 var(--chakra-colors-gray-200)',
			}
		: undefined;

	const stickyFeatureHeaderSx = inModal
		? {
				...stickyHeaderSx,
				left: 0,
				zIndex: 3,
			}
		: stickyHeaderSx;

	const stickyFeatureCellSx = (rowBg) =>
		inModal
			? {
					position: 'sticky',
					left: 0,
					zIndex: 1,
					bg: rowBg,
					boxShadow: '1px 0 0 var(--chakra-colors-gray-100)',
				}
			: undefined;

	return (
		<Box>
			<Box
				mt={inModal ? 0 : 12}
				bg='white'
				border='1px solid'
				borderColor='gray.200'
				borderRadius={inModal ? 'xl' : '2xl'}
				overflowX='auto'
				overflowY={inModal ? 'auto' : undefined}
				maxH={inModal ? 'calc(90vh - 14rem)' : undefined}
				boxShadow={inModal ? 'none' : 'sm'}>
				<Table variant='simple' size='sm' minW='720px'>
					<Thead bg='gray.50'>
						<Tr>
							<Th
								textTransform='none'
								fontSize='xs'
								color='gray.600'
								borderColor='gray.200'
								minW='200px'
								verticalAlign='bottom'
								pb={3}
								sx={stickyFeatureHeaderSx}>
								Features
							</Th>
							{plans.map((plan) => (
								<Th
									key={plan.key}
									textAlign='center'
									textTransform='none'
									borderColor='gray.200'
									minW='130px'
									verticalAlign='bottom'
									pb={3}
									sx={stickyHeaderSx}>
									<PlanColumnHeader plan={plan} isYearly={isYearly} />
								</Th>
							))}
						</Tr>
					</Thead>
					<Tbody>
						{featureRows.map((row, index) => {
							const rowBg = index % 2 === 0 ? 'white' : 'gray.50';

							return (
								<Tr key={row.name} bg={rowBg}>
									<Td
										fontWeight='600'
										fontSize={compact ? '11px' : 'xs'}
										color='gray.700'
										borderColor='gray.100'
										py={compact ? 1.5 : 2}
										sx={stickyFeatureCellSx(rowBg)}>
										{row.name}
									</Td>
									{plans.map((plan) => (
										<Td
											key={`${row.name}-${plan.key}`}
											textAlign='center'
											borderColor='gray.100'
											py={compact ? 1.5 : 2}
											px={2}>
											<FeatureValue
												value={row.values?.[plan.key]}
												compact={compact}
											/>
										</Td>
									))}
								</Tr>
							);
						})}
					</Tbody>
				</Table>
			</Box>
		</Box>
	);
};

export default PublicPricingMatrix;
